import { useCallback, useMemo } from 'react';
import { resolveVisibleFields, BUILT_IN_VIEW_FIELDS } from '../lib/viewFields';

// Binds a view's config.visible_fields to the ViewTabs action set.
// `actions.updateView(id, { config })` persists via the underlying driver
// (shared project catalog or per-user UIPreferences).
export function useViewFieldConfig(activeView, actions, customFieldDefs = []) {
  const visibleFields = useMemo(
    () => resolveVisibleFields(activeView, customFieldDefs),
    [activeView, customFieldDefs],
  );

  // Ordered list of every field toggle-able in the picker (built-ins first,
  // then custom fields) along with current visibility state.
  const allFields = useMemo(() => {
    const visibleSet = new Set(visibleFields);
    return [
      ...BUILT_IN_VIEW_FIELDS.map(f => ({
        key: f.key,
        i18n: f.i18n,
        label: null,
        isCustom: false,
        visible: visibleSet.has(f.key),
      })),
      ...customFieldDefs.map(def => ({
        key: `cf_${def.id}`,
        i18n: null,
        label: def.name,
        isCustom: true,
        visible: visibleSet.has(`cf_${def.id}`),
      })),
    ];
  }, [visibleFields, customFieldDefs]);

  const commit = useCallback((nextFields) => {
    if (!activeView || !actions?.updateView) return;
    const nextConfig = { ...(activeView.config || {}), visible_fields: nextFields };
    actions.updateView(activeView.id, { config: nextConfig });
  }, [activeView, actions]);

  const toggleField = useCallback((key) => {
    const set = new Set(visibleFields);
    if (set.has(key)) set.delete(key); else set.add(key);
    commit(Array.from(set));
  }, [visibleFields, commit]);

  const setFieldVisible = useCallback((key, visible) => {
    const set = new Set(visibleFields);
    if (visible) set.add(key); else set.delete(key);
    commit(Array.from(set));
  }, [visibleFields, commit]);

  return { visibleFields, allFields, toggleField, setFieldVisible };
}
